import { AddIcon, Button, Checkbox, CloseIcon, Dropdown, DropdownItemProps, Flex, Input, Menu, ProviderConsumer, tabListBehavior, ThemePrepared } from '@fluentui/react-northstar';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { AppKBStateContext, kbState, useSetKBState } from '../AppKBState';
import { AppStateKBValue, KBData } from '../AppState';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { Breadcrumbs } from '../shared/components/BreadcrumbNavigation';
import { LoadingScreen } from '../shared/components/LoadingScreen';
import { Toolbar } from '../shared/components/Toolbar';
import { EditorComponent } from '../shared/components/Wysiwyg/Editor';
import { discardCatalogImages, useDiscardOnLeave } from '../shared/components/Wysiwyg/CatalogImage';
import { kbDataService } from '../shared/services/kb.service';
import { LICENSE_FEATURE } from '../shared/utils/constants';
import UpgradeSVG from './../../svg/upgrade.svg'
import { ScopePermissionPicker } from '../Rbac/ScopePermissionPicker';
import { ScopeErrorMessage } from '../Rbac/ScopePermissionNoAccess';
import { haveScopedMembersChanged } from '../Rbac/AvailableTo/scopedMembers';
import { themeNames } from '@fluentui/react-teams';
import _ from 'lodash';
import useUnsavedChangesWarning from '../shared/hooks/useUnsavedChangesWarning';

interface IKbItem {
  id: number,
  questions: string[],
  answer: string,
  status: string,
  source: string,
  agentOnly: boolean,
  enableOffering: boolean,
  textAnswer: string,
  templateAnswer: string,
  useTemplate: boolean,
  ScopedMembers:any[],
  EnableScoping:boolean
}
interface IRoItem {
  id: number,
  requestOffering: string,
  requestDescription: string,
  order: number,
  serviceOfferingId: number
}
interface IKbTab {
  state: AppStateKBValue,
  globalTheme: ThemePrepared<any>,
  data: IKbItem,
  templates: DropdownItemProps[],
  updateItem: (data: IKbItem) => void,
  catalogEnabled: boolean,
  templateEnabled: boolean,
  onImageUploaded?: (id: string) => void,
  onUploadingChange?: (uploading: boolean) => void
}
interface IRoTab {
  state: AppStateKBValue,
  globalTheme: ThemePrepared<any>,
  data: IRoItem,
  offerings: DropdownItemProps[],
  updateItem: (data: IRoItem) => void;
  onImageUploaded?: (id: string) => void;
  onUploadingChange?: (uploading: boolean) => void;
}
const KnowledgeTab = ({ state, globalTheme, data, templates, updateItem, catalogEnabled, templateEnabled, onImageUploaded, onUploadingChange }: IKbTab) => {
  const { t } = useTranslation();
  const [statuses, setStatuses] = useState<Array<DropdownItemProps>>([]);
  

  useEffect(() => {
    setStatuses(['Draft', 'In Review', 'Active'].map(item => {
      let itemValue = item.replace(/\s+/g, "");
      return {
        accessibilityItemProps: {
          id: itemValue,
          answerValue: itemValue
        },
        header: item,
        selected: itemValue === data.status
      } as DropdownItemProps;
    }));
  }, [data.status]);

  return (<Flex column gap="gap.small" className="px-6 overflow-auto" style={{ height: 'calc(100vh - 170px)' }}>
    <div>
      <label className={`mb-2 block`}>{t('knowledge.add-knowledge-modal.phrases')}<span style={{ color: 'red', marginLeft: 2 }}>*</span></label>
      {data.questions.map((q, index) =>
        <Flex key={index}>
          <Input fluid inverted showSuccessIndicator={false} placeholder={t('knowledge.add-knowledge-modal.placeholder')} maxLength={1000}
            style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }} variables={{ background: globalTheme.siteVariables.colorScheme.default.background2 }}
            className={`mb-2`} value={q} onChange={(e, p) => {
              data.questions[index] = p.value;
              updateItem(data);
            }}
          />
          <Button icon={<CloseIcon />} text iconOnly title={t('knowledge.add-knowledge-modal.button.remove')} styles={{ alignSelf: 'center' }}
            onClick={() => {
              data.questions = (data.questions.length > 1) ? data.questions.slice(0, index).concat(data.questions.slice(index + 1)) : [''];
              updateItem(data);
            }} />
        </Flex>
      )}
      <Button icon={<AddIcon />} text content={t('knowledge.add-knowledge-modal.add-alternative-phrasing')} className={'mb-2 cui-btn'} style={{ color: globalTheme.siteVariables.colorScheme.brand.foreground }}
        {...{
          onClick: () => {
            data.questions.push('');
            updateItem(data);
          }
        }} />
    </div>
    <div>
      <label className={`mb-2`}>{t('knowledge.knowledge-table.status')}</label>
      <Dropdown style={{ background: globalTheme.siteVariables.colorScheme.default.background, width: 'inherit' }} fluid items={statuses} value={statuses.filter(x => x.selected)}
        onChange={(e, p) => {
          data.status = p.value ? p.value['accessibilityItemProps'].answerValue : '';;
          updateItem(data);
        }} />
    </div>
    <div>
      <Checkbox toggle label={t('knowledge.knowledge-table.agent-only')} checked={data.agentOnly} onChange={() => { data.agentOnly = !data.agentOnly; updateItem(data); }} />
    </div>
    <div>
      <Checkbox toggle label={t('knowledge.knowledge-table.enable-ro')} disabled={!catalogEnabled} checked={data.enableOffering} onChange={() => { data.enableOffering = !data.enableOffering; updateItem(data); }} />
      {!catalogEnabled && <UpgradeSVG height={25} width={25} className='inline-block' />}
    </div>
    <div>
      <span className="italic" dangerouslySetInnerHTML={{ __html: t('knowledge.knowledge-table.offering-subcopy', { color: globalTheme.siteVariables.colorScheme.brand.foregroundActive })}} />
    </div>
    <div>
      <Checkbox label={t('knowledge.add-knowledge-modal.use-template')} toggle checked={data.useTemplate} disabled={!templateEnabled} onChange={() => { data.useTemplate = !data.useTemplate; updateItem(data); }} />
      {!templateEnabled && <UpgradeSVG height={25} width={25} className='inline-block' />}
    </div>
    {!data.useTemplate && (<EditorComponent markdownString={data.textAnswer} maxLength={25000} height="40vh" growToFit useBorder={true} allowImageUpload keepBlankLines onImageUploaded={onImageUploaded} onUploadingChange={onUploadingChange} onChange={(val: string) => {
      data.textAnswer = val;
      data.answer = val;
      updateItem(data);
    }} />)}
    {data.useTemplate && (<Dropdown fluid defaultValue={templates.find(x => x.selected)} placeholder={(templates.find(x => x.selected)?.header?.toString() ?? '')} style={{ background: globalTheme.siteVariables.colorScheme.default.background }} items={templates}
      onChange={(e, p) => {
        data.templateAnswer = p.value ? p.value['accessibilityItemProps'].answerValue : '';
        data.answer = data.templateAnswer;
        updateItem(data);
      }} />)}
  </Flex>);
}
const RequestOfferingTab = ({ state, globalTheme, data, offerings, updateItem, onImageUploaded, onUploadingChange }: IRoTab) => {
  const { t } = useTranslation();

  return (<Flex column gap="gap.small" className="px-6 overflow-auto" style={{ height: 'calc(100vh - 170px)' }}>
    <label className={`mb-2 block`}>{t('knowledge.add-knowledge-modal.offering-title')}<span style={{ color: 'red', marginLeft: 2 }}>*</span></label>
    <Input fluid inverted showSuccessIndicator={false} placeholder={t('knowledge.add-knowledge-modal.offering-placeholder')} maxLength={1000}
      style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }} variables={{ background: globalTheme.siteVariables.colorScheme.default.background2 }}
      className={`mb-2`} value={data.requestOffering} onChange={(e, p) => {
        data.requestOffering = p.value;
        updateItem(data);
      }} />

    <label className={`mb-2 block`}>{t('knowledge.add-knowledge-modal.offering-description')}</label>
    <EditorComponent markdownString={data.requestDescription} maxLength={25000} height="200px" useBorder={true} allowImageUpload keepBlankLines onImageUploaded={onImageUploaded} onUploadingChange={onUploadingChange} onChange={(val: string) => { data.requestDescription = val; updateItem(data); }} />

    <Flex gap="gap.large">
      <Flex.Item grow>
        <Flex column>
          <label className={`mb-2 block`}>{t('knowledge.add-knowledge-modal.service-offering')}<span style={{ color: 'red', marginLeft: 2 }}>*</span></label>
          <Dropdown fluid defaultValue={offerings.find(x => x.selected)} placeholder={(offerings.find(x => x.selected)?.header?.toString() ?? '')} style={{ background: globalTheme.siteVariables.colorScheme.default.background }} items={offerings}
            onChange={(e, p) => {
              data.serviceOfferingId = p.value ? p.value['accessibilityItemProps'].id : '';
              updateItem(data);
            }} />
            <span className="pt-1 pb-2 italic" dangerouslySetInnerHTML={{ __html: t('knowledge.knowledge-table.service-offering-copy', { color: globalTheme.siteVariables.colorScheme.brand.foregroundActive })}} />
        </Flex>
      </Flex.Item>
      <Flex.Item grow>
        <Flex column className='grow-1'>
          <label className={`mb-2 block`}>{t('knowledge.add-knowledge-modal.order')}</label>
          <Input type="number" fluid inverted style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }} variables={{ background: globalTheme.siteVariables.colorScheme.default.background2 }}
            className={`mb-2`} value={data.order} onChange={(e, p) => { data.order = parseInt(p.value, 10); updateItem(data); }} />
        </Flex>
      </Flex.Item>
    </Flex>
  </Flex>);
}


const SharingTab = ({ globalTheme, data, updateItem }: IKbTab) => {
  const { t } = useTranslation();
  return (<Flex column gap="gap.small" className="px-6 overflow-auto" style={{ height: 'calc(100vh - 170px)' }}>
    <div className={globalTheme.siteVariables.theme===themeNames.Dark ? 'SCScopePickerBackgroundDark' : 'SCScopePickerBackgroundLight'}>
      <ScopePermissionPicker
        data={data}
        description={t('scoping.description-catalog')}
        changed={(members,isEnabled)=>{
           data.EnableScoping = isEnabled;
           data.ScopedMembers = members;
           updateItem(data);
        }}
      />
    </div>
  </Flex>
  );
}

export const KnowledgeItem: React.FC = (props) => {
  const state = kbState();
  const setKbState = useSetKBState();
  const { t } = useTranslation();
  const history = useHistory();
  const { kbId } = useParams<any>();
  const [data, setData] = useState<IKbItem & IRoItem>({ id: 0, source: 'Editorial', agentOnly: false, enableOffering: false, answer: '', textAnswer: '', templateAnswer: '', questions: [''], status: 'Draft', useTemplate: false, requestOffering: '', requestDescription: '', order: 0, serviceOfferingId: 0,ScopedMembers:[], EnableScoping:false });
  const [originalData, setOriginalData] = useState<IKbItem & IRoItem>(data);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [templates, setTemplates] = useState<DropdownItemProps[]>([]);
  const [offerings, setOfferings] = useState<DropdownItemProps[]>([]);
  const [noAccess, setNoAccess] = useState(false);

  const [kbTab, setKbTab] = useState<number>(0);
  const [tabItems, setTabItems] = useState([
    { key: 'kb', content: <div>{t('knowledge.catalog-item')}</div> },
    { key: 'ro', content: <div>{t('knowledge.ropairs')}</div>, disabled: false }
  ]);
  const [loadingState, setLoadingState] = useState({ message: t('knowledge.loading-knowledge-item'), isLoading: true });
  const [catalogEnabled, setCatalogEnabled] = useState<boolean>(false);
  const [templateEnabled, setTemplateEnabled] = useState<boolean>(false);

  // Every image uploaded on this visit. Handed back after a save - the server keeps the ones a catalog item uses - or
  // all of them when the page is left without saving.
  const uploadedImages = useRef<string[]>([]);
  const imageUploaded = (id: string) => { uploadedImages.current.push(id); };
  const discardUploadedImages = () => {
    discardCatalogImages(uploadedImages.current);
    uploadedImages.current = [];
  };
  useEffect(() => discardUploadedImages, []);
  useDiscardOnLeave(uploadedImages);
  // Save waits for pictures still uploading - see the editor's onUploadingChange.
  const [imagesUploading, setImagesUploading] = useState(false);

  const [Prompt, setDirty] = useUnsavedChangesWarning();
  const setFormDirty: (isDirty: boolean) => void = setDirty as (isDirty: boolean) => void;

  const fetchTemplates = async () => {
    let templates = state.templates;
    if (templates?.length == 0) {
      templates = await kbDataService.fetchTemplates();
      setKbState(prev => ({ ...prev, templates: templates }));
    }
    setTemplates(templates);
  };
  const fetchOfferings = async () => {
    let offers = state.serviceOfferings;
    if (offers?.length == 0) {
      offers = await kbDataService.fetchOfferings();
      setKbState(prev => ({ ...prev, serviceOfferings: offers }));
    }
    setOfferings(offers);
  }
  const loadData = async () => {
    setLoadingState(p => ({ ...p, isLoading: true }));

    const results = await Promise.all([getCachedFeature(LICENSE_FEATURE.ServiceCatalog), getCachedFeature(LICENSE_FEATURE.TemplateDeflection), getCachedFeature(LICENSE_FEATURE.PermissionScoping)]);

    setCatalogEnabled(results[0]);
    setTemplateEnabled(results[1]);
    const scopingEnabled = results[2];

    if (scopingEnabled)
      setTabItems(prev => [...prev, { key: 'sharing', content: <div>{t('scoping.header')}</div>, disabled: false }]);
    

    let item=data;
    try {
      const results = await Promise.all([fetchTemplates(), fetchOfferings(), kbDataService.fetchAnswerByIdAPI(kbId)]);
      let tempData = results[2].data;
      item = tempData;
      item.ScopedMembers=JSON.parse(tempData.ScopedMembers);
      // ScopePermissionPicker derives its own enabled state the same way
      // (ScopedMembers.length>0) and reports it back through `changed` the
      // moment the Sharing tab mounts - matching it here keeps originalData
      // in agreement so opening the tab alone doesn't look like an edit.
      item.EnableScoping = item.ScopedMembers?.length > 0;

      delete item['@odata.context'];
      delete item['metadata'];

      if (item.id!=0){
        if (item.answer?.match(/^ApplyTemplate:\d+$/gi)) {
          item.useTemplate = true;
          item.templateAnswer = item.answer;
        } else {
          item.useTemplate = false;
          item.textAnswer = item.answer;
        }
        setData(item);
        setOriginalData(_.cloneDeep(item));
      }

      setTabItems(prev => { prev[1].disabled = !item.enableOffering; return [...prev]; });
      setTemplates(prev => prev.map(i => ({ ...i, selected: (item.templateAnswer === i['accessibilityItemProps'].answerValue) })));
      setOfferings(prev => prev.filter(i => !i.accessibilityItemProps.archived || i.accessibilityItemProps.id === item.serviceOfferingId).map(i => ({ ...i, selected: (item.serviceOfferingId === i['accessibilityItemProps'].id) })));
      
    } catch {
      if (kbId>0 && scopingEnabled)
      {
        setNoAccess(true);
      }
      
    }

    setLoadingState(p => ({ ...p, isLoading: false }));
  };

  const isFormUpdated = (): boolean => {
    const { ScopedMembers: updatedScopedMembers, ...updatedRest } = data;
    const { ScopedMembers: initialScopedMembers, ...initialRest } = originalData;

    if (haveScopedMembersChanged(updatedScopedMembers, initialScopedMembers))
      return true;

    return !_.isEqual(updatedRest, initialRest);
  };

  useEffect(() => {
    setFormDirty(isFormUpdated());
  }, [data, originalData]);

  useEffect(() => {
    loadData();
  }, []);

  const updateKbData = (d: IKbItem) => {
    setData(p => ({ ...p, ...d }));
    setTabItems(prev => { prev[1].disabled = !d.enableOffering; return [...prev]; });
    setTemplates(prev => prev.map(i => ({ ...i, selected: (d.templateAnswer === i['accessibilityItemProps'].answerValue) })));
  }
  const updateRoData = (d: IRoItem) => {
    setData(p => ({ ...p, ...d }));
    setOfferings(prev => prev.map(i => ({ ...i, selected: (d.serviceOfferingId === i['accessibilityItemProps'].id) })));
  }

  const saveIsDisabled = (): boolean =>
    isSaving ||
    imagesUploading ||
    !isFormUpdated() ||
    (data.questions.length == 0 || data.questions.every(x => x.trim().length == 0)) ||
    (data.enableOffering && ((data.requestOffering?.trim().length ?? 0) == 0 || (data.serviceOfferingId ?? 0) == 0)) ||
    (data.useTemplate && (data.templateAnswer?.trim().length ?? 0) == 0) ||
    (!data.useTemplate && (data.textAnswer?.trim().length ?? 0) == 0) ||
    (data.EnableScoping && data.ScopedMembers.length == 0);

  const saveData = async () => {
    setIsSaving(true);
    setLoadingState(state => ({ message: t('knowledge.loading-knowledge-message'), isLoading: true }));

    try {
      data.id = kbId;
      data.source = data.useTemplate ? 'Template' : 'Editorial';
      data.serviceOfferingId = data.serviceOfferingId === 0 ? null : data.serviceOfferingId;
      delete data.EnableScoping;

      // kbDataService mutates its argument (it JSON.stringifies ScopedMembers
      // in place for the API payload) - pass a clone so that doesn't corrupt
      // our own state's ScopedMembers array out from under us.
      const payload = _.cloneDeep(data);
      if (kbId > 0)
        await kbDataService.updateSingleKB(payload as unknown as KBData);
      else
        await kbDataService.addAPI([payload]);

      setOriginalData(_.cloneDeep(data));
      setFormDirty(false);
      discardUploadedImages();
      history.push(`/servicecatalog`);
    } catch {

    } finally {
      setLoadingState(state => ({ message: t('knowledge.loading-knowledge-message'), isLoading: false }));
      setIsSaving(false);
    }
  };

  const navs: Breadcrumbs = {
    breadcrumbs: [
      {
        title: t('knowledge.title'),
        link: '/servicecatalog'
      },
      {
        title: t('knowledge.edit-knowledge'),
        link: ""
      }
    ]
  };

  return <>
    <AppKBStateContext.Consumer>
      {state => (<ProviderConsumer render={globalTheme => (
        <>
          <Toolbar globalTheme={globalTheme} title={t('knowledge.title')} saveText={t('common.buttons.save')} saveCommand={saveData} breadcrumbs={navs} disableSave={saveIsDisabled()} />
          {noAccess &&
            <ScopeErrorMessage msgType='Kb' />
          }

          {!noAccess && loadingState.isLoading && <LoadingScreen message={loadingState.message} />}
          {!noAccess && !loadingState.isLoading && <div>
            <Menu defaultActiveIndex={kbTab} items={tabItems} underlined primary accessibility={tabListBehavior} aria-label="Knowledge " onActiveIndexChange={(e, p: any) => { setKbTab(p.activeIndex); }} />
            {kbTab === 0 && <KnowledgeTab data={data} globalTheme={globalTheme} state={state} templates={templates} updateItem={updateKbData} catalogEnabled={catalogEnabled} templateEnabled={templateEnabled} onImageUploaded={imageUploaded} onUploadingChange={setImagesUploading} />}
            {kbTab === 1 && <RequestOfferingTab data={data} globalTheme={globalTheme} state={state} offerings={offerings} updateItem={updateRoData} onImageUploaded={imageUploaded} onUploadingChange={setImagesUploading} />}
            {kbTab === 2 && <SharingTab data={data} globalTheme={globalTheme} state={state} updateItem={updateKbData} templates={templates} catalogEnabled={false} templateEnabled={false} />}
          </div>}
        </>
      )} />)}
    </AppKBStateContext.Consumer>
    {Prompt}
  </>;
};